import config from 'config/config';
import { createAxiosInstance } from 'utils/axiosConfig';
import {
    MatchAnalytics,
    MVPData,
    HighlightsData,
    PlayerProgression,
    TeamAnalytics,
    WeaponStats,
} from 'types/analytics';

class AnalyticsService {
    host = config.API_BASE_URL;
    apiPath = '/v1/analytics';

    private joinUrl(...parts: string[]): string {
        return parts
            .map((part, i) => {
                if (i === 0) return part.replace(/\/+$/, '');
                return part.replace(/^\/+|\/+$/g, '');
            })
            .filter(Boolean)
            .join('/');
    }

    private axiosInstance = createAxiosInstance({
        timeout: 15000,
    });

    /**
     * Fetch complete match analytics
     */
    async fetchMatchAnalytics(matchId: string, etag?: string): Promise<{
        data: MatchAnalytics;
        etag: string | null;
    }> {
        const url = this.joinUrl(this.host, this.apiPath, 'matches', matchId);
        const headers: Record<string, string> = {};
        
        if (etag) {
            headers['If-None-Match'] = etag;
        }

        const response = await this.axiosInstance.get<MatchAnalytics>(url, { headers });
        const responseEtag = response.headers['etag'] || response.headers['ETag'] || null;

        return {
            data: response.data,
            etag: responseEtag,
        };
    }

    /**
     * Fetch MVP analysis
     */
    async fetchMVP(matchId: string, etag?: string): Promise<{
        data: MVPData;
        etag: string | null;
    }> {
        const url = this.joinUrl(this.host, this.apiPath, 'matches', matchId, 'mvp');
        const headers: Record<string, string> = {};
        
        if (etag) {
            headers['If-None-Match'] = etag;
        }

        const response = await this.axiosInstance.get<MVPData>(url, { headers });
        const responseEtag = response.headers['etag'] || null;

        return {
            data: response.data,
            etag: responseEtag,
        };
    }

    /**
     * Fetch match highlights
     */
    async fetchHighlights(matchId: string, limit: number = 15, etag?: string): Promise<{
        data: HighlightsData;
        etag: string | null;
    }> {
        const url = this.joinUrl(this.host, this.apiPath, 'matches', matchId, 'highlights');
        const headers: Record<string, string> = {};
        
        if (etag) {
            headers['If-None-Match'] = etag;
        }

        const response = await this.axiosInstance.get<HighlightsData>(url, {
            headers,
            params: { limit },
        });
        const responseEtag = response.headers['etag'] || null;

        return {
            data: response.data,
            etag: responseEtag,
        };
    }

    /**
     * Fetch player progression
     */
    async fetchProgression(matchId: string, steamId?: string, etag?: string): Promise<{
        data: PlayerProgression | { mostImproved: PlayerProgression | null; mostDeclined: PlayerProgression | null; rankings?: any[]; summary?: any };
        etag: string | null;
    }> {
        const path = steamId
            ? [this.apiPath, 'matches', matchId, 'progression', steamId]
            : [this.apiPath, 'matches', matchId, 'progression'];
        
        const url = this.joinUrl(this.host, ...path);
        const headers: Record<string, string> = {};
        
        if (etag) {
            headers['If-None-Match'] = etag;
        }

        const response = await this.axiosInstance.get(url, { headers });
        const responseEtag = response.headers['etag'] || null;

        return {
            data: response.data,
            etag: responseEtag,
        };
    }

    /**
     * Fetch team analytics
     */
    async fetchTeamAnalytics(matchId: string, etag?: string): Promise<{
        data: TeamAnalytics;
        etag: string | null;
    }> {
        const url = this.joinUrl(this.host, this.apiPath, 'matches', matchId, 'teams');
        const headers: Record<string, string> = {};
        
        if (etag) {
            headers['If-None-Match'] = etag;
        }

        const response = await this.axiosInstance.get<TeamAnalytics>(url, { headers });
        const responseEtag = response.headers['etag'] || null;

        return {
            data: response.data,
            etag: responseEtag,
        };
    }

    /**
     * Fetch weapon statistics
     */
    async fetchWeaponStats(matchId: string, etag?: string): Promise<{
        data: WeaponStats;
        etag: string | null;
    }> {
        const url = this.joinUrl(this.host, this.apiPath, 'matches', matchId, 'weapons');
        const headers: Record<string, string> = {};
        
        if (etag) {
            headers['If-None-Match'] = etag;
        }

        const response = await this.axiosInstance.get<WeaponStats>(url, { headers });
        const responseEtag = response.headers['etag'] || null;

        return {
            data: response.data,
            etag: responseEtag,
        };
    }
}

const analyticsService = new AnalyticsService();

export default analyticsService;

